import React from 'react';
import { ArrowRight, CheckCircle, Award, Clock, Instagram } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-[85vh] flex items-center overflow-hidden">
      {/* Diagonal Background Design */}
      <div className="absolute inset-0">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-[#E6F7FF]"></div>
        
        {/* Diagonal shapes - inspired by banner */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-br from-[#003366] to-[#004d99] opacity-5 transform skew-x-12 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-2/3 bg-gradient-to-tr from-[#00CCDD] to-[#00AABB] opacity-5 transform -skew-x-12 -translate-x-1/4"></div>
        </div>

        {/* Geometric accents */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-gradient-to-br from-[#00CCDD]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-[#004d99]/10 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Content */}
          <div className="space-y-8 lg:pr-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-black to-[#00CCDD] text-white px-6 py-3 rounded-full shadow-lg">
              <Award className="w-5 h-5" />
              <span className="font-semibold text-sm tracking-wide">MEISTERBETRIEB MIT ERFAHRUNG</span>
            </div>
            
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="text-black">Ihr zuverlässiger</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-black to-[#00CCDD]">
                  Partner
                </span>
                <br />
                <span className="text-black">für schlüssel-</span>
                <br />
                <span className="text-black">fertiges Bauen</span>
              </h1>
            </div>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
              Qualität, Präzision und Zuverlässigkeit seit über 15 Jahren. 
              Von der ersten Idee bis zum fertigen Traumhaus – wir begleiten Sie auf jedem Schritt.
            </p>

            {/* Rhein-Main Partner Hinweis */}
            <div className="bg-gradient-to-r from-gray-100 to-[#E6F7FF] border-l-4 border-[#00CCDD] px-4 py-3 rounded-r-lg">
              <p className="text-gray-700 text-sm">
                <span className="font-semibold">Auch im Gebiet Rhein-Main tätig:</span> THS Bauprojekte GmbH – vertreten durch einen Partner in der Region Mainz und Umgebung.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { id: 'exp', icon: CheckCircle, text: 'Über 15 Jahre Erfahrung' },
                { id: 'meister', icon: Award, text: '3 Meister im Betrieb' },
                { id: 'termin', icon: Clock, text: 'Termintreue garantiert' },
                { id: 'projekte', icon: CheckCircle, text: '180+ erfolgreiche Projekte' }
              ].map((item) => (
                <div key={item.id} className="flex items-center space-x-3 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-black to-[#00CCDD] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-gradient-to-r from-black to-[#00CCDD] hover:from-black hover:to-black text-white text-lg px-8 py-7 group shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Kostenloses Angebot
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('projects')}
                size="lg"
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white text-lg px-8 py-7 transition-all duration-300"
              >
                Referenzen ansehen
              </Button>
            </div>

            {/* Instagram CTA */}
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <a
                href="https://www.instagram.com/ths.bauprojekte"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-6 py-4 rounded-xl hover:scale-105 transition-transform duration-300 shadow-lg group"
              >
                <Instagram className="w-6 h-6" />
                <div className="text-left">
                  <p className="font-bold text-sm">Folgen Sie uns auf Instagram</p>
                  <p className="text-xs opacity-90">Aktuelle Projekte & Behind-the-Scenes</p>
                </div>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              
              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/4917659885691?text=Hallo%2C%20ich%20interessiere%20mich%20f%C3%BCr%20Ihre%20Bauleistungen."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-[#25D366] text-white px-6 py-4 rounded-xl hover:scale-105 transition-transform duration-300 shadow-lg group"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <div className="text-left">
                  <p className="font-bold text-sm">WhatsApp Anfrage</p>
                  <p className="text-xs opacity-90">Schnelle Antwort garantiert</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Image with Floating Card */}
          <div className="relative lg:mt-0 mt-12">
            {/* Main Image Container with Diagonal Border */}
            <div className="relative">
              {/* Diagonal background accent */}
              <div className="absolute -top-6 -right-6 w-full h-full bg-gradient-to-br from-[#004d99] to-[#00CCDD] rounded-2xl transform rotate-3"></div>
              
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-500">
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc2OTY0MTEwMHww&ixlib=rb-4.1.0&q=85"
                  alt="TH Bauprojekte Team"
                  className="w-full h-[600px] object-cover"
                />
                {/* Image overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#003366]/50 to-transparent"></div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-6 max-w-xs border-2 border-[#00CCDD]/30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Erfolgsquote</p>
                    <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#004d99] to-[#00CCDD]">
                      100%
                    </p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#004d99] to-[#00CCDD] flex items-center justify-center">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Zufriedene Kunden in Bonn und Umgebung
                </p>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-8 -left-8 w-24 h-24 border-4 border-[#00CCDD] rounded-full opacity-30"></div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border-4 border-[#004d99] rounded-full opacity-20"></div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#004d99] rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-1.5 bg-[#00CCDD] rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};